import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth, useMe, isAdmin } from './auth';
import { useProjects, usePerm, EXTERNAL_ROLES, TRAIL_ROLES } from './data';
import { Icon, Modal, useToast } from './ui/kit';
import { Mark, ChangePasswordForm } from './pages/Auth';
import { initials, ROLE_LABEL } from './format';
import { useMyNotifications } from './fin';

const LAST = 'sp:ultimoProjeto';
const THEME = 'sp:tema';
const store = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sem armazenamento */ } } };

function useTheme() {
  const [theme, setTheme] = useState<string>(() => store.get(THEME) ?? 'auto');
  useEffect(() => {
    if (theme === 'auto') document.documentElement.removeAttribute('data-theme'); else document.documentElement.setAttribute('data-theme', theme);
    store.set(THEME, theme);
  }, [theme]);
  const dark = theme === 'dark' || (theme === 'auto' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  return { dark, toggle: () => setTheme(dark ? 'light' : 'dark') };
}

export function Layout() {
  const me = useMe(); const { logout } = useAuth(); const toast = useToast();
  const { projectId } = useParams(); const nav = useNavigate(); const loc = useLocation();
  const projects = useProjects();
  const [open, setOpen] = useState(false); const [confirmReset, setConfirmReset] = useState(false); const [menu, setMenu] = useState(false); const [pwd, setPwd] = useState(false);
  const theme = useTheme();
  const current = projectId ?? store.get(LAST) ?? '';
  const valid = projects.data?.some(p => p.id === current) ? current : '';
  const perm = usePerm(valid);
  useEffect(() => { if (projectId) store.set(LAST, projectId); }, [projectId]);
  useEffect(() => { setOpen(false); setMenu(false); }, [loc.pathname]);

  const switchProject = (id: string) => {
    if (!id) { nav('/'); return; }
    const sub = projectId ? loc.pathname.split('/').slice(3).join('/') : '';
    nav(`/p/${id}${sub ? '/' + sub : ''}`);
  };
  /** O que está pendente para você em cada área do projeto (avisos ainda não resolvidos nem tratados) */
  const notes = useMyNotifications();
  const pending = new Map<string, number>(); let pendingElsewhere = 0;
  for (const n of notes.data?.items ?? []) {
    if (n.status === 'Resolvida' || n.status === 'Tratada') continue;
    if (n.projectId !== valid) { pendingElsewhere++; continue; }
    const area = '/' + (n.link.split('/')[3] ?? ''); pending.set(area, (pending.get(area) ?? 0) + 1);
  }
  const dot = (k: number, where: string) => k > 0 ? <span className="nav-dot" aria-hidden="true" title={`${k} item(ns) esperando você ${where}`}>{k > 99 ? '99+' : k}</span> : null;
  const pItem = (to: string, label: string, icon: string, show = true) => valid && show
    ? <NavLink to={`/p/${valid}${to}`} end={to === ''} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><Icon name={icon} /><span className="grow">{label}</span>{dot(pending.get(to) ?? 0, `em ${label}`)}</NavLink>
    : null;

  return (
    <div className="shell">
      <nav className={'nav' + (open ? ' open' : '')} aria-label="Navegação principal">
        <div className="brand"><Mark /><div><div className="brand-name">VMM</div><div className="brand-sub">Engenharia e<br />Consultoria</div></div></div>
        <div className="nav-group">
          <NavLink to="/" end className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><Icon name="folder" /><span className="grow">Projetos</span>{dot(pendingElsewhere, 'em outros projetos')}</NavLink>
        </div>
        {valid && (
          <div className="nav-group">
            <div className="nav-group-label">Projeto</div>
            {pItem('', 'Visão geral', 'home')}
            {pItem('/equipe', 'Equipe', 'users', !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/disciplinas', 'Disciplinas', 'layers', perm.can('disciplinas', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/eap', 'EAP', 'tree', perm.can('eap', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/documentos', EXTERNAL_ROLES.includes(perm.role ?? '') ? 'Documentos para aprovação' : 'Documentos', 'file', perm.can('documentos', 'view'))}
            {pItem('/cronograma', 'Cronograma', 'gantt', perm.can('cronograma', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/medicoes', 'Medições', 'measure', perm.can('medicoes', 'view'))}
            {pItem('/custos', 'Custos e CAPEX', 'money', perm.can('custos', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/riscos', 'Riscos', 'alert', perm.can('riscos', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/mudancas', 'Mudanças', 'swap', perm.can('mudancas', 'view') && !EXTERNAL_ROLES.includes(perm.role ?? ''))}
            {pItem('/relatorios', 'Relatórios', 'report', perm.can('relatorios', 'view'))}
            {pItem('/avisos', 'Avisos do projeto', 'bell', ['gerente', 'diretor', 'diretor_fin', 'auditor', 'coord_eng'].includes(perm.role ?? ''))}
            {pItem('/rastro', 'Rastro de documentos', 'trail', TRAIL_ROLES.includes(perm.role ?? ''))}
            {pItem('/auditoria', 'Auditoria', 'shield', ['gerente', 'diretor', 'diretor_fin', 'auditor'].includes(perm.role ?? ''))}
          </div>
        )}
        {me.cashAccess && me.cashAccess !== 'nenhum' && (
          <div className="nav-group">
            <div className="nav-group-label">Empresa</div>
            <NavLink to="/caixa" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><Icon name="money" />Fluxo de caixa</NavLink>
          </div>
        )}
        {isAdmin(me) && (
          <div className="nav-group">
            <div className="nav-group-label">Administração</div>
            <NavLink to="/admin/empresas" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><Icon name="building" />Empresas</NavLink>
            <NavLink to="/admin/usuarios" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}><Icon name="users" />Usuários</NavLink>
          </div>
        )}
        <div className="nav-foot">SIDERPROJ · gestão de projetos · versão 0.5</div>
      </nav>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" aria-label="Abrir menu" onClick={() => setOpen(true)}><Icon name="menu" /></button>
          <div className="grow">
            <select className="input proj-select" aria-label="Projeto" value={valid} onChange={e => switchProject(e.target.value)}>
              <option value="">{projects.data?.length ? 'Selecione um projeto' : 'Nenhum projeto'}</option>
              {projects.data?.map(p => { const k = (notes.data?.items ?? []).filter(n => n.projectId === p.id && n.status !== 'Resolvida' && n.status !== 'Tratada').length; return <option key={p.id} value={p.id}>{p.code} · {p.name}{k ? ` (${k} pendente${k > 1 ? 's' : ''})` : ''}</option>; })}
            </select>
          </div>
          {perm.role && <span className="badge info role-badge" title="Seu perfil neste projeto">{ROLE_LABEL[perm.role] ?? perm.role}</span>}
          <Bell />
          <button className="icon-btn" aria-label={theme.dark ? 'Usar tema claro' : 'Usar tema escuro'} onClick={theme.toggle}><Icon name={theme.dark ? 'sun' : 'moon'} /></button>
          <div style={{ position: 'relative' }}>
            <button className="user-chip" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(m => !m)}><span className="avatar">{initials(me.name)}</span><span className="uname">{me.name}</span></button>
            {menu && (
              <div className="popover" role="menu" style={{ position: 'absolute', right: 0, top: 44 }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}><strong>{me.name}</strong><div className="small muted">{me.email}</div></div>
                <button className="pop-item" role="menuitem" onClick={() => { setPwd(true); setMenu(false); }}>Trocar senha</button>
                <button className="pop-item" role="menuitem" onClick={() => void logout()}>Sair</button>
              </div>
            )}
          </div>
        </header>
        {import.meta.env.MODE === 'demo' && <div className="preview-banner">Versão de demonstração: tudo roda neste navegador e os dados ficam só aqui. <button className="btn sm" onClick={() => { if (!confirmReset) { setConfirmReset(true); setTimeout(() => setConfirmReset(false), 4000); return; } void import('./demo/server').then(m => { m.resetDemo(); location.reload(); }); }}>{confirmReset ? 'Clique de novo para apagar tudo' : 'Restaurar dados de exemplo'}</button></div>}
        {projectId && valid && perm.locked && <div className="callout warn locked-banner" role="status"><strong>Projeto {perm.status?.toLowerCase()}: só consulta.</strong> Lançamentos só em projetos Ativos ou Em planejamento.{perm.can('projeto', 'edit') ? ' Para lançar, mude a situação do projeto em Visão geral › Editar.' : ' Para lançar, o gerente precisa mudar a situação do projeto.'}</div>}
        <main className="content"><Outlet /></main>
      </div>
      {pwd && <Modal title="Trocar senha" size="narrow" onClose={() => setPwd(false)}><ChangePasswordForm onDone={() => { setPwd(false); toast.ok('Senha alterada. As outras sessões foram encerradas.'); }} /></Modal>}
    </div>
  );
}

export function PageHead({ eyebrow, title, sub, children }: { eyebrow?: string; title: string; sub?: React.ReactNode; children?: React.ReactNode }) {
  return <div className="page-head"><div>{eyebrow && <div className="eyebrow">{eyebrow}</div>}<h1 className="page-title">{title}</h1>{sub && <div className="page-sub">{sub}</div>}</div>{children && <div className="toolbar">{children}</div>}</div>;
}

/** Sino com a contagem de avisos não lidos. */
function Bell() {
  const q = useMyNotifications(); const n = q.data?.unread ?? 0;
  return <NavLink to="/avisos" className="icon-btn bell" aria-label={n ? `${n} aviso(s) novo(s)` : 'Avisos'} title="Avisos"><Icon name="bell" />{n > 0 && <span className="dot">{n > 99 ? '99+' : n}</span>}</NavLink>;
}
